<template>
  <div
    id="appRoot"
    :key="`app-${tenancy.id}`"
  >
    <confirm ref="confirm"/>
    <template v-if="!$route.meta.public">
      <v-app
        id="inspire"
        :dark="darkMode"
        :light="!darkMode"
        class="app"
      >
        <base-layout/>
      </v-app>
    </template>
    <template v-else>
      <keep-alive>
        <router-view />
      </keep-alive>
    </template>
  </div>
</template>
<script>

import Confirm from './components/Confirm'
import AppEvents from './event'
import { mapGetters, mapActions } from 'vuex'
import StoreService from './modules/Settings/service'
import BaseLayout from '@/components/BaseLayout'

export default {
  components: {
    BaseLayout,
    Confirm
  },

  data () {
    return {
      expanded: true,
      rightDrawer: false,
      isConnected: false,
      socketMessage: '',
      darkMode: false,
      generalSettings: null
    }
  },

  computed: {
    ...mapGetters(['fetchingStatus', 'storesLoaded', 'stores', 'tenancy'])
  },

  created () {
    AppEvents.forEach(item => {
      this.$on(item.name, item.callback)
    })

    this.$radio.$on('app-login-success', async () => {
      await this.initStores()
      await this.initApp()
      this.$router.push({ path: 'orders' })
    })

    this.$radio.$on('item-saved', event => {
      this.$toast(this.$t(event.message))
    })

    this.$radio.$on('toggle-dark', () => {
      this.darkMode = !this.darkMode
    })

    this.$radio.$on('api-error', ({ error }) => {
      if (error.response) {
        if (error.response.status >= 500) {
          this.$router.push({ path: '/500' })
        } else {
          this.$toast.error(this.$t(error.message))
        }
      } else if (error.request) {
        // The request was made but no response was received
        this.$toast.error(this.$t('No response from the server'))
      } else {
        // Something happened in setting up the request that triggered an Error
        this.$toast.error(this.$t('Something went wrong'))
      }
    })

    this.$radio.$on('app-logout', () => {
      this.setStore({})

      localStorage.removeItem('refresh-token')

      if (!['PasswordReset', 'ForgotPassword'].includes(this.$route.name)) {
        this.$router.replace({ path: '/login' })
      }
    })
  },

  async mounted () {
    await this.loadUser(this.$http.make('auth:user'))

    this.$root.$confirm = this.$refs.confirm.open
    this.setCurrency(process.env.VUE_APP_DEFAULT_CURRENCY)
    this.setLocale(process.env.VUE_APP_DEFAULT_LOCALE)

    if (localStorage.getItem('user-token')) {
      await this.initStores()
      await this.initApp()
    }

    this.$radio.$on('store-change', async () => {
      await this.initApp()
    })
  },

  updated () {
    this.$nextTick(function () {
      this.$root.$confirm = this.$refs.confirm.open
    })
  },

  methods: {
    ...mapActions([
      'setStore',
      'addStore',
      'setFetchingStoresStatus',
      'setStoresLoadedStatus',
      'setCurrency',
      'setLocale',
      'setCountries',
      'setAllowedLists',
      'setDefaultLang',
      'setLanguagesList',
      'setTimezone',
      'setDateFormat',
      'setTimezoneLoading'
    ]),
    ...mapActions({
      'loadUser': 'auth/LOAD_CURRENT_USER'
    }),

    async initStores () {
      await this.setFetchingStoresStatus(true)
      const fetchedStores = await StoreService.loadStores()

      if (!Array.isArray(fetchedStores) || fetchedStores.length === 0) {
        await this.setFetchingStoresStatus(false)
        this.$router.push({ name: 'settings-store-installer' })
        return
      }

      await this.fetchStoresDetails(fetchedStores)
    },

    async initApp () {
      this.generalSettings = await this.$http.make('settings').find('general')

      await this.initCountries()
      await this.initLanguages()
      await this.initTimezone()
      this.setTimezoneLoading(false)
    },

    async initLanguages () {
      const languages = await this.$http.make('language').get()

      this.setLanguagesList(languages)
      this.setDefaultLang(this.generalSettings.locale.language)
    },

    async initTimezone () {
      await this.setTimezone({
        level: 'console',
        data: this.generalSettings.timezone.console
      })
      await this.setTimezone({
        level: 'global',
        data: this.generalSettings.timezone.global
      })
      await this.setDateFormat({
        level: 'console',
        data: this.generalSettings.date_format.console
      })
      await this.setDateFormat({
        level: 'global',
        data: this.generalSettings.date_format.global
      })
    },

    async initCountries () {
      const countries = await this.$http.make('country').get()
      this.setCountries(countries)

      const settings = await this.$http.make('settings').find('localisation/countries')
      this.setAllowedLists(settings)
    },

    async fetchStoresDetails (fetchedStores) {
      await Promise.all(fetchedStores.map(async store => {
        let fetchedDetails = await this.$http.stores.find(store.id)
        fetchedDetails['id'] = store.id
        return this.addStore(fetchedDetails)
      }))
      await this.setStore(fetchedStores[0].code)
      await this.setStoresLoadedStatus(true)
      await this.setFetchingStoresStatus(false)
    }
  }
}
</script>

<style lang="stylus" scoped>
.setting-fab
  top: 50% !important;
  right: 0;
  border-radius: 0
</style>
